(() => {
  'use strict';
  const percentages = Object.freeze({ 1: 0, 2: 0.15, 4: 0.20 });

  /** Shopify Liquid/Ajax prices use integer hundredths, including zero-decimal currencies. */
  function offerTotal(price, quantity) {
    if (!Number.isSafeInteger(price) || price < 0 || !Object.hasOwn(percentages, quantity)) {
      throw new Error('Invalid offer');
    }
    const total = Math.round(price * quantity * (1 - percentages[quantity]));
    if (!Number.isSafeInteger(total)) throw new Error('Invalid price');
    return total;
  }

  function matchesLine(line, attempt) {
    return line && String(line.variant_id || line.id) === String(attempt.variantId)
      && line.quantity === attempt.quantity
      && line.properties?._cv2_mm_attempt === attempt.id
      && !line.selling_plan_allocation && !line.selling_plan;
  }

  /** Reconcile this buy box's cart line on each click; lock only in-flight work. */
  class Purchase {
    constructor(config, dependencies) {
      this.config = config;
      this.dependencies = dependencies;
      this.key = 'cv2-mm-purchase:' + config.productId;
      this.state = 'ready';
      this.attempt = null;
      this.storageReady = true;
      if (config.preview) return;
      try {
        const saved = JSON.parse(dependencies.storage.getItem(this.key));
        if (saved?.id && Number.isSafeInteger(Number(saved.variantId)) && Object.hasOwn(percentages, saved.quantity)) this.attempt = saved;
      } catch { /* Private browsing or stale storage must not block purchasing. */ }
    }

    get locked() { return this.state === 'pending'; }

    save() {
      try {
        if (this.attempt) this.dependencies.storage.setItem(this.key, JSON.stringify(this.attempt));
        else this.dependencies.storage.removeItem(this.key);
      } catch { /* The in-memory attempt still protects the current request. */ }
    }

    result(state, reason) {
      this.state = state;
      return { state, reason, attempt: this.attempt };
    }

    async request(path, method, payload) {
      const url = new URL(path, this.dependencies.origin);
      const expected = method === 'GET' ? ['/cart.js'] : ['/cart/add.js', '/cart/change.js'];
      if (url.origin !== this.dependencies.origin || !expected.some(route => url.pathname.endsWith(route))) throw new Error('Invalid cart route');
      if (method === 'GET') url.searchParams.set('cv2_mm_check', String(Date.now()));
      const controller = new AbortController();
      let timer;
      try {
        return await Promise.race([
          (async () => {
            const response = await this.dependencies.fetch(url.href, {
              method, credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
              headers: { Accept: 'application/json', ...(payload ? { 'Content-Type': 'application/json' } : {}) },
              ...(payload ? { body: JSON.stringify(payload) } : {})
            });
            if (!response.ok) throw new Error('Cart response was not confirmed');
            return response.json();
          })(),
          new Promise((_, reject) => {
            timer = setTimeout(() => { controller.abort(); reject(new Error('Cart check timed out')); }, this.dependencies.timeout || 20000);
          })
        ]);
      } finally { clearTimeout(timer); }
    }

    ownedLine(cart) {
      if (cart.currency !== this.config.currency || !Array.isArray(cart.items)) throw new Error('Cart currency or contents changed');
      const lines = cart.items.filter(line => line.properties?._cv2_mm_attempt === this.attempt?.id);
      if (lines.length > 1) throw new Error('Cart line needs review');
      return lines[0];
    }

    confirm(cart) {
      const line = this.ownedLine(cart);
      const discount = line?.line_level_discount_allocations?.some(allocation => Number.isSafeInteger(allocation.amount) && allocation.amount > 0);
      if (!matchesLine(line, this.attempt) || !line.key || !Number.isSafeInteger(line.final_line_price)
        || line.final_line_price < 0 || line.final_line_price > this.attempt.expected
        || (this.attempt.quantity > 1 && !discount)) throw new Error('Quantity or offer needs cart review');
      this.attempt.lineKey = line.key;
      this.attempt.confirmed = true;
      this.save();
      return line;
    }

    async submit(variant, quantity) {
      if (this.config.preview) return this.result('preview');
      if (this.locked) return { state: this.state, attempt: this.attempt };
      let expected;
      try {
        if (!variant?.available || !variant.oneTimeAllowed || !Number.isSafeInteger(Number(variant.id))) throw new Error('Unavailable');
        expected = offerTotal(variant.price, quantity);
      } catch { return this.result('error', 'selection'); }
      this.state = 'pending';
      try {
        if (this.attempt) {
          const cart = await this.request(this.config.cartUrl, 'GET');
          const line = this.ownedLine(cart);
          if (line && String(line.variant_id || line.id) === String(variant.id) && !line.selling_plan_allocation && !line.selling_plan) {
            this.attempt = { ...this.attempt, variantId: variant.id, quantity, expected, currency: this.config.currency, confirmed: false };
            this.save();
            const updated = line.quantity === quantity ? cart : await this.request(
              this.config.cartChangeUrl || this.config.cartUrl.replace(/cart\.js$/, 'cart/change.js'),
              'POST', { id: line.key, quantity }
            );
            this.confirm(updated);
            return this.result('success');
          }
          if (line) throw new Error('Previous cart line needs review');
          this.attempt = null;
          this.save();
        }
        this.attempt = { id: this.dependencies.attemptId(), variantId: variant.id, quantity, expected, currency: this.config.currency };
        if (!this.attempt.id) throw new Error('Missing attempt identifier');
        this.save();
        const response = await this.request(this.config.cartAddUrl, 'POST', {
          items: [{ id: variant.id, quantity, properties: { _cv2_mm_attempt: this.attempt.id } }]
        });
        const added = response.items || [response];
        if (added.length !== 1 || !matchesLine(added[0], this.attempt) || !added[0].key) throw new Error('Add response unconfirmed');
        this.confirm(await this.request(this.config.cartUrl, 'GET'));
        return this.result('success');
      } catch { return this.result('uncertain'); }
    }

    async recover() {
      if (this.config.preview) return this.result('preview');
      if (!this.attempt || this.locked) return { state: this.state, attempt: this.attempt };
      this.state = 'pending';
      try {
        const cart = await this.request(this.config.cartUrl, 'GET');
        if (!this.ownedLine(cart)) {
          this.attempt = null; this.save();
          return this.result('ready');
        }
        this.confirm(cart);
        return this.result('success');
      } catch { return this.result('uncertain'); }
    }

    async restart() { return this.recover(); }
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { offerTotal, Purchase };
  if (typeof customElements === 'undefined' || customElements.get('cv2-mm-buybox')) return;

  class CV2MMBuybox extends HTMLElement {
    #events;
    #observer;
    #frame;
    #active = 0;
    #near = false;
    #navigationEpoch = 0;
    #data;
    #purchase;
    #leaving = false;

    connectedCallback() {
      if (this.#events) return;
      this.#events = new AbortController();
      this.#setupGallery();
      this.form = this.querySelector('[data-cv2-mm-form]');
      if (!this.form) return;
      this.submitButton = this.querySelector('[data-cv2-mm-submit]');
      this.status = this.querySelector('[data-cv2-mm-status]');
      this.variantInput = this.querySelector('[data-cv2-mm-variant]');
      this.quantityInput = this.querySelector('[data-cv2-mm-quantity]');
      const signal = this.#events.signal;
      // Capture before any enhancement so malformed configuration cannot submit a preview form.
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        if (this.#purchase) this.#submit();
      }, { signal, capture: true });
      try {
        this.#data = JSON.parse(this.querySelector('[data-cv2-mm-data]').textContent);
        this.#data.preview = this.hasAttribute('data-cv2-mm-preview') || this.#data.preview === true;
        this.money = new Intl.NumberFormat(this.#data.locale || 'en', { style: 'currency', currency: this.#data.currency, currencyDisplay: 'narrowSymbol' });
        this.#purchase = new Purchase(this.#data, {
          origin: location.origin,
          storage: { getItem: key => sessionStorage.getItem(key), setItem: (key, value) => sessionStorage.setItem(key, value), removeItem: key => sessionStorage.removeItem(key) },
          fetch: (...args) => fetch(...args), attemptId: () => crypto.randomUUID()
        });
      } catch {
        this.submitButton.disabled = true;
        this.status.textContent = 'The purchase options could not load. Please reload this page.';
        return;
      }
      const previousAttempt = this.#purchase.attempt;
      this.quantityInput.value = String(previousAttempt?.quantity || 1);
      if (previousAttempt) this.variantInput.value = String(previousAttempt.variantId);
      this.variantInput.addEventListener('change', () => {
        if (this.#purchase.locked) return;
        this.quantityInput.value = '1';
        this.status.textContent = '';
        this.#render();
      }, { signal });
      for (const button of this.querySelectorAll('[data-cv2-mm-bundle]')) {
        button.addEventListener('click', () => {
          if (this.#purchase.locked) return;
          this.quantityInput.value = button.dataset.cv2MmBundle;
          this.status.textContent = '';
          this.#render();
        }, { signal });
      }
      this.querySelector('[data-cv2-mm-recover]').addEventListener('click', async () => {
        const recovery = this.#purchase.recover();
        this.#render();
        this.#feedback(await recovery);
        this.#render();
      }, { signal });
      this.querySelector('[data-cv2-mm-restart]').addEventListener('click', async () => {
        const restart = this.#purchase.restart();
        this.#render();
        const result = await restart;
        if (result.state === 'ready') this.quantityInput.value = '1';
        this.#feedback(result);
        this.#render();
      }, { signal });
      window.addEventListener('pageshow', event => {
        this.#leaving = false;
        this.#near = true;
        this.#hydrateNearby();
        const activeImage = this.slides?.[this.#active]?.querySelector('img');
        if (activeImage) activeImage.loading = 'eager';
        if (this.track) requestAnimationFrame(() => this.track.scrollBy({
          left: this.slides[this.#active].getBoundingClientRect().left - this.track.getBoundingClientRect().left, behavior: 'instant'
        }));
        if (!this.#purchase.locked) this.#feedback({ state: 'ready' });
        this.#render();
      }, { signal });
      window.addEventListener('pagehide', () => { this.#navigationEpoch += 1; }, { signal });
      this.querySelector('[data-cv2-mm-bundles]').hidden = false;
      this.querySelector('[data-cv2-mm-fallback-note]').hidden = true;
      this.#feedback({ state: 'ready' });
      this.#render();
    }

    disconnectedCallback() {
      this.#navigationEpoch += 1;
      this.#events?.abort();
      this.#events = null;
      this.#observer?.disconnect();
      cancelAnimationFrame(this.#frame);
    }

    #variant() {
      return this.#data.variants.find(variant => String(variant.id) === this.variantInput.value);
    }

    #money(value, currency = this.#data.currency) {
      const money = currency === this.#data.currency ? this.money
        : new Intl.NumberFormat(this.#data.locale || 'en', { style: 'currency', currency, currencyDisplay: 'narrowSymbol' });
      return money.format(value / 100) + ' ' + currency;
    }

    #render() {
      const variant = this.#variant();
      const quantity = Number(this.quantityInput.value);
      const valid = variant?.available && variant.oneTimeAllowed && Object.hasOwn(percentages, quantity);
      const pending = this.#purchase.state === 'pending';
      const locked = this.#purchase.locked || this.#leaving;
      this.form.setAttribute('aria-busy', String(pending));
      this.variantInput.disabled = locked;
      this.submitButton.disabled = locked || !valid || !this.#purchase.storageReady;
      this.querySelector('[data-cv2-mm-submit-label]').textContent = pending ? this.#data.labels.adding
        : !variant?.available ? this.#data.labels.soldOut : !valid ? this.#data.labels.unavailable : this.#data.labels.add;
      const recover = this.querySelector('[data-cv2-mm-recover]');
      recover.disabled = pending;
      this.querySelector('[data-cv2-mm-restart]').disabled = this.#purchase.state !== 'absent';
      for (const button of this.querySelectorAll('[data-cv2-mm-bundle]')) {
        const count = Number(button.dataset.cv2MmBundle);
        button.setAttribute('aria-pressed', String(count === quantity));
        button.disabled = locked || !variant?.available || !variant.oneTimeAllowed;
        const saved = null;
        if (saved && count === quantity) button.querySelector('[data-cv2-mm-bundle-price]').textContent = this.#money(saved.expected, saved.currency);
        else if (variant) button.querySelector('[data-cv2-mm-bundle-price]').textContent = this.#money(offerTotal(variant.price, count));
      }
      if (!variant || !Object.hasOwn(percentages, quantity)) return;
      const attempt = null;
      const currentTotal = offerTotal(variant.price, quantity);
      const total = attempt?.expected ?? currentTotal;
      const currency = attempt?.currency || this.#data.currency;
      const priceChanged = attempt && (total !== currentTotal || currency !== this.#data.currency);
      const quantityText = quantity + ' ' + (quantity === 1 ? this.#data.labels.unit : this.#data.labels.units);
      for (const node of this.querySelectorAll('[data-cv2-mm-total]')) node.textContent = this.#money(total, currency);
      for (const node of this.querySelectorAll('[data-cv2-mm-compare]')) {
        node.textContent = this.#money(variant.compareAt * quantity);
        node.hidden = Boolean(priceChanged) || variant.compareAt * quantity <= total;
        node.setAttribute('aria-label', this.#data.labels.regular + ': ' + node.textContent);
      }
      const heroQuantity = this.querySelector('[data-cv2-mm-hero-quantity]');
      if (heroQuantity) heroQuantity.textContent = quantityText;
      this.querySelector('[data-cv2-mm-per-unit]').textContent = this.#money(Math.round(total / quantity), currency) + ' / ' + this.#data.labels.unit;
      this.querySelector('[data-cv2-mm-today]').textContent = quantityText + ' - ' + this.#money(total, currency);
      const offerTitle = this.#data.offers.find(offer => offer.quantity === quantity)?.title || '';
      this.querySelector('[data-cv2-mm-offer-details]').textContent = priceChanged ? this.#data.labels.previousSelection + ': ' + offerTitle : offerTitle;
      if (!this.#purchase.storageReady) this.#feedback({ state: 'error', reason: 'storage' });
    }

    #feedback(result) {
      const labels = this.#data.labels;
      const messages = { preview: labels.preview, pending: labels.adding, success: labels.success, uncertain: labels.uncertain, absent: labels.absent, ready: labels.restarted, error: result.reason === 'storage' ? labels.storageError : labels.error };
      this.status.textContent = messages[result.state] || '';
      this.status.dataset.state = result.state;
      const mayHaveItem = ['success', 'uncertain', 'absent'].includes(result.state);
      this.querySelector('[data-cv2-mm-cart-links]').hidden = !mayHaveItem || this.#data.preview;
      this.querySelector('[data-cv2-mm-checkout]').hidden = !['success', 'uncertain'].includes(result.state);
      this.querySelector('[data-cv2-mm-recover]').hidden = !mayHaveItem;
      this.querySelector('[data-cv2-mm-restart]').hidden = result.state !== 'absent';
    }

    async #submit() {
      if (this.#purchase.locked || this.#leaving) return;
      const navigationEpoch = this.#navigationEpoch;
      const submitted = this.#purchase.submit(this.#variant(), Number(this.quantityInput.value));
      this.#render();
      if (this.#purchase.state === 'pending') this.#feedback({ state: 'pending' });
      const result = await submitted;
      this.#feedback(result);
      this.#render();
      if (result.state !== 'success' || !this.isConnected || navigationEpoch !== this.#navigationEpoch) return;
      this.dispatchEvent(new CustomEvent('cv2-mm:cart-added', { bubbles: true, detail: { quantity: result.attempt.quantity } }));
      this.#leaving = true;
      this.#render();
      window.location.assign(this.querySelector('[data-cv2-mm-checkout]').href);
    }

    #setupGallery() {
      this.track = this.querySelector('[data-cv2-mm-track]');
      this.slides = [...this.querySelectorAll('[data-cv2-mm-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-cv2-mm-thumb]')];
      if (!this.track || !this.slides.length) return;
      this.previous = this.querySelector('[data-cv2-mm-previous]');
      this.next = this.querySelector('[data-cv2-mm-next]');
      const signal = this.#events.signal;
      const navigate = (index, focus = false) => {
        const next = Math.min(this.slides.length - 1, Math.max(0, index));
        this.#near = true;
        this.#showSlide(next, true);
        this.track.scrollBy({ left: this.slides[next].getBoundingClientRect().left - this.track.getBoundingClientRect().left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        if (focus) this.thumbs[next]?.focus({ preventScroll: true });
      };
      for (const button of [this.previous, this.next]) if (button) button.hidden = false;
      this.previous?.addEventListener('click', () => navigate(this.#active - 1), { signal });
      this.next?.addEventListener('click', () => navigate(this.#active + 1), { signal });
      for (const thumb of this.thumbs) {
        thumb.addEventListener('click', event => { event.preventDefault(); navigate(Number(thumb.dataset.cv2MmThumb)); }, { signal });
      }
      this.querySelector('.cv2-mm-buybox__gallery').addEventListener('keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        const direction = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
        const targets = { ArrowLeft: this.#active - direction, ArrowRight: this.#active + direction, Home: 0, End: this.slides.length - 1 };
        if (!(event.key in targets)) return;
        event.preventDefault();
        navigate(targets[event.key], Boolean(event.target.closest('[data-cv2-mm-thumb]')));
      }, { signal });
      this.track.addEventListener('pointerdown', () => { this.#near = true; this.#hydrateNearby(); }, { signal, passive: true });
      if ('IntersectionObserver' in window) {
        this.#observer = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this.#near = true;
          this.#hydrateNearby();
          this.#observer.disconnect();
        }, { rootMargin: '300px 0px' });
        this.#observer.observe(this.track);
      }
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.#frame);
        this.#frame = requestAnimationFrame(() => {
          const rect = this.track.getBoundingClientRect();
          const center = rect.left + rect.width / 2;
          let nearest = 0;
          let distance = Infinity;
          for (const [index, slide] of this.slides.entries()) {
            const bounds = slide.getBoundingClientRect();
            const delta = Math.abs(bounds.left + bounds.width / 2 - center);
            if (delta < distance) { distance = delta; nearest = index; }
          }
          this.#showSlide(nearest, true);
        });
      }, { signal, passive: true });
      window.addEventListener('resize', () => this.track.scrollBy({
        left: this.slides[this.#active].getBoundingClientRect().left - this.track.getBoundingClientRect().left, behavior: 'instant'
      }), { signal, passive: true });
      this.#showSlide(this.#active, false);
    }

    #hydrateSlide(index) {
      const template = this.slides[index]?.querySelector('[data-cv2-mm-slide-image]');
      if (template) template.replaceWith(template.content.cloneNode(true));
    }

    #hydrateNearby() {
      for (const index of [this.#active - 1, this.#active, this.#active + 1]) this.#hydrateSlide(index);
    }

    #showSlide(index, announce) {
      const changed = index !== this.#active;
      this.#active = index;
      this.#hydrateSlide(index);
      if (this.#near) this.#hydrateNearby();
      for (const [position, slide] of this.slides.entries()) slide.setAttribute('aria-hidden', String(position !== index));
      for (const [position, thumb] of this.thumbs.entries()) {
        if (position === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      }
      if (this.previous) this.previous.disabled = index === 0;
      if (this.next) this.next.disabled = index === this.slides.length - 1;
      const thumb = this.thumbs[index];
      if (thumb && changed) {
        const rail = thumb.parentElement;
        const rect = thumb.getBoundingClientRect();
        const bounds = rail.getBoundingClientRect();
        if (rect.left < bounds.left || rect.right > bounds.right) rail.scrollBy({ left: rect.left - bounds.left - (bounds.width - rect.width) / 2, behavior: 'instant' });
      }
      if (announce && changed) this.querySelector('[data-cv2-mm-gallery-status]').textContent = this.slides[index].getAttribute('aria-label');
    }
  }
  customElements.define('cv2-mm-buybox', CV2MMBuybox);
})();
